import { Badge } from '@up2stay/up2promo-ui';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { NotificationInboxPanel } from '@/core/components/notifications/notification-inbox-panel';
import { UserInvitationsPanel } from '@/core/components/notifications/user-invitations-panel';
import { UserNotificationPreferencesPanel } from '@/core/components/notifications/user-notification-preferences-panel';
import { PageHeader } from '@/core/components/page-header';
import { AnimatedUnderlineTabs } from '@/core/components/ui/tabs-29';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePendingInvitations } from '@/core/hooks/use-pending-invitations';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { AppIonPage } from '@/core/components/app-ion-page';

export type NotificationHubTab = 'inbox' | 'preferences' | 'invitations';

function parseTab(value: string | null): NotificationHubTab {
  if (value === 'preferences' || value === 'invitations') return value;
  return 'inbox';
}

function NotificationsBreadcrumb({
  domainLabel,
  notificationsLabel,
}: {
  domainLabel: string;
  notificationsLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {notificationsLabel}
    </>
  );
}

export function UserNotificationsPage() {
  const dict = useDictionary();
  const notificationsDict = dict.workspace.notifications;
  const prefsDict = dict.settings.notifications;
  const { navigateShell } = useNavigateShell();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const activeTab = parseTab(searchParams.get('tab'));
  const { data: invitations } = usePendingInvitations();
  const pendingInvitationsCount = invitations?.length ?? 0;

  const setTab = (tab: NotificationHubTab) => {
    const params = new URLSearchParams(location.search);
    if (tab === 'inbox') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    const query = params.toString();
    navigateShell(
      query
        ? `${shellPaths.moreUserNotifications}?${query}`
        : shellPaths.moreUserNotifications,
      { intent: 'updateQuery' },
    );
  };

  const tabs = useMemo(
    () => [
      {
        name: notificationsDict.hubTabs.inbox,
        value: 'inbox',
        content: <NotificationInboxPanel dict={notificationsDict} />,
      },
      {
        name: (
          <span className="inline-flex items-center gap-2">
            {notificationsDict.hubTabs.invitations}
            {pendingInvitationsCount > 0 ? (
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {pendingInvitationsCount}
              </Badge>
            ) : null}
          </span>
        ),
        value: 'invitations',
        content: <UserInvitationsPanel />,
      },
      {
        name: notificationsDict.hubTabs.preferences,
        value: 'preferences',
        content: <UserNotificationPreferencesPanel dict={prefsDict} />,
      },
    ],
    [notificationsDict, prefsDict, pendingInvitationsCount],
  );

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout className="min-w-0 overflow-x-hidden">
          <PageHeader
            breadcrumb={
              <NotificationsBreadcrumb
                domainLabel={dict.nav.account}
                notificationsLabel={dict.nav.notifications}
              />
            }
            title={notificationsDict.title}
          />
          <AnimatedUnderlineTabs
            className="w-full min-w-0"
            tabs={tabs}
            value={activeTab}
            onValueChange={(value) => setTab(parseTab(value))}
          />
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
